import { Immutable } from "immer";
import React, { useEffect, useState } from "react";

import { IOptionCode } from "../../../models/catalogue";
import { useAppSelector } from "../../../store/reducers";
import { slugify } from "../../../utils/text";
import { ConditionalBlockContent } from "../ConditionalBlockContent";
import { getOptionValueSet } from "../utils";

type IProps = Immutable<{
    attributeCode: IOptionCode;
    attributeValue: string;
    isInitiallyVisible: boolean;
    // Legacy JSON content path (#33285 pre-consolidation): a serialized
    // ``serialize_content_blocks`` payload rendered via ``ConditionalBlockContent``.
    // OPTIONAL so the consolidated page-body assemblies can instead pass real
    // React ``children`` (the nested StreamField rendered through the components
    // map). When ``children`` is provided it wins and ``contentSegments`` is
    // ignored; when it is absent the legacy ``contentSegments`` path renders
    // exactly as before (mirrors PDPSelectedRootProductConditionalBlock, which
    // already accepts children).
    contentSegments?: string;
    children?: React.ReactNode;
}>;

export const PDPSelectedVariantConditionalBlock = (
    props: IProps,
): React.ReactNode => {
    const selectedValues = useAppSelector(
        (state) => state.configurator.ui.optionValues,
    );

    // Consumer pattern: use server-provided initial visibility until Redux is populated
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => {
        setHasMounted(true);
    }, []);

    const optionValueSet = getOptionValueSet(
        selectedValues[props.attributeCode],
    );
    const isSelected =
        hasMounted && Object.keys(selectedValues).length > 0
            ? optionValueSet.has(props.attributeValue)
            : props.isInitiallyVisible;

    // Build IDs for relationship to tab controls. These IDs must match the IDs used by PDPChangeVariantOptionBlock.
    const tabControlID = slugify(
        `change-variant-block-${props.attributeCode}-${props.attributeValue}`,
    );
    const tabPanelID = slugify(
        `variant-conditional-block-${props.attributeCode}-${props.attributeValue}`,
    );

    return (
        <div
            id={tabPanelID}
            role="tabpanel"
            aria-labelledby={tabControlID}
            aria-hidden={!isSelected}
            style={{ display: isSelected ? "block" : "none" }}
        >
            {props.children !== undefined ? (
                props.children
            ) : (
                <ConditionalBlockContent
                    contentSegments={props.contentSegments ?? "[]"}
                />
            )}
        </div>
    );
};
